import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";

const PaymentPhaseAdmin = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [phases, setPhases] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPhase, setNewPhase] = useState({
    label: "",
    priceTaxIncluded: "",
    priceTaxExcluded: "",
    priceIdTaxIncluded: "",
    priceIdTaxExcluded: "",
    start: "",
    end: "",
    phaseNumber: "",
    visible: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return navigate("/home");

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.data();
      if (!userData?.isAdmin) return navigate("/home");

      setIsAdmin(true);

      const snap = await getDocs(collection(db, "paymentPhases2025Late"));
      const data = snap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((phase) => phase.phaseNumber === 3); // ← 第3期のみ表示

      setPhases(data);
      setLoading(false);
    };

    fetchData();
  }, [navigate]);

  const handleAdd = async () => {
    try {
      await addDoc(collection(db, "paymentPhases2025Late"), {
        ...newPhase,
        phaseNumber: parseInt(newPhase.phaseNumber),
        visible: newPhase.visible,
        start: new Date(newPhase.start),
        end: newPhase.end ? new Date(newPhase.end) : null,
        createdAt: serverTimestamp(),
      });
      setNewPhase({
        label: "",
        priceTaxIncluded: "",
        priceTaxExcluded: "",
        priceIdTaxIncluded: "",
        priceIdTaxExcluded: "",
        start: "",
        end: "",
        phaseNumber: "",
        visible: true,
      });
      setDialogOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("追加エラー:", err);
    }
  };

  const handleToggleVisible = async (phaseId, currentVisible) => {
    try {
      const newVisible = !currentVisible;
      await updateDoc(doc(db, "paymentPhases2025Late", phaseId), {
        visible: newVisible,
      });

      // フェーズ一覧を即時更新
      setPhases((prev) =>
        prev.map((phase) =>
          phase.id === phaseId ? { ...phase, visible: newVisible } : phase
        )
      );

      console.log("表示状態を更新しました:", newVisible);
    } catch (err) {
      console.error("表示状態の更新に失敗しました:", err);
    }
  };

  const formatDateTimeLocal = (value) => {
    if (!value) return "";

    let date;

    // Firestore Timestamp 対応（.toDateがあるなら実行）
    if (typeof value.toDate === "function") {
      date = value.toDate();
    } else {
      date = new Date(value);
    }

    if (isNaN(date.getTime())) return "";

    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleUpdateStart = async (id, value) => {
    try {
      const newStart = new Date(value);
      if (isNaN(newStart.getTime())) return;
      await updateDoc(doc(db, "paymentPhases2025Late", id), {
        start: newStart,
      });

      setPhases((prev) =>
        prev.map((p) => (p.id === id ? { ...p, start: newStart } : p))
      );
    } catch (err) {
      console.error("開始日時の更新エラー:", err);
    }
  };

  const handleUpdateEnd = async (id, value) => {
    try {
      const newEnd = value ? new Date(value) : null;
      if (value && isNaN(newEnd.getTime())) return;
      await updateDoc(doc(db, "paymentPhases2025Late", id), { end: newEnd });

      setPhases((prev) =>
        prev.map((p) => (p.id === id ? { ...p, end: newEnd } : p))
      );
    } catch (err) {
      console.error("終了日時の更新エラー:", err);
    }
  };

  if (loading) return <CircularProgress sx={{ m: 5 }} />;

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
      >
        <Typography variant="h5">支払いフェーズ管理</Typography>
        <IconButton onClick={() => setDialogOpen(true)}>
          <AddCircleIcon color="primary" fontSize="large" />
        </IconButton>
      </Box>

      <Box mt={4}>
        <Typography variant="subtitle1">第3期フェーズ</Typography>
        {phases.map((p) => (
          <Paper key={p.id} sx={{ p: 2, mt: 2 }}>
            <Typography fontWeight="bold">{p.label}</Typography>
            <Typography>第 {p.phaseNumber ?? "-"} 期</Typography>
            <Typography>税込価格: {p.priceTaxIncluded}</Typography>
            <Typography>税抜価格: {p.priceTaxExcluded}</Typography>
            <Typography>Price ID（国内）: {p.priceIdTaxIncluded}</Typography>
            <Typography>Price ID（海外）: {p.priceIdTaxExcluded}</Typography>
            <Box mt={2}>
              <TextField
                label="開始日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formatDateTimeLocal(p.start)}
                onChange={(e) => handleUpdateStart(p.id, e.target.value)}
              />

              <TextField
                label="終了日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formatDateTimeLocal(p.end)}
                onChange={(e) => handleUpdateEnd(p.id, e.target.value)}
              />
            </Box>

            <Box mt={2} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>表示状態：</Typography>
              <Switch
                checked={p.visible}
                onChange={() => handleToggleVisible(p.id, p.visible)}
                color="primary"
              />
            </Box>
          </Paper>
        ))}
      </Box>

      {/* 新規フェーズ追加ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>新しいフェーズを追加</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                label="表示ラベル"
                fullWidth
                value={newPhase.label}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, label: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="税込価格（国内）"
                fullWidth
                value={newPhase.priceTaxIncluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceTaxIncluded: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="税抜価格（海外）"
                fullWidth
                value={newPhase.priceTaxExcluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceTaxExcluded: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Stripe Price ID（国内）"
                fullWidth
                value={newPhase.priceIdTaxIncluded}
                onChange={(e) =>
                  setNewPhase({
                    ...newPhase,
                    priceIdTaxIncluded: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Stripe Price ID（海外）"
                fullWidth
                value={newPhase.priceIdTaxExcluded}
                onChange={(e) =>
                  setNewPhase({
                    ...newPhase,
                    priceIdTaxExcluded: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="開始日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newPhase.start}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, start: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="終了日時（任意）"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newPhase.end}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, end: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="期（フェーズ番号）"
                value={newPhase.phaseNumber}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, phaseNumber: e.target.value })
                }
                SelectProps={{ native: true }}
              >
                <option value="">選択してください</option>
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>表示する</Typography>
              <Switch
                checked={newPhase.visible}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, visible: e.target.checked })
                }
                color="primary"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAdd} variant="contained">
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentPhaseAdmin;
